import {useMemo, useState} from "react";

import EmptyData from "@/components/EmptyData";
import VLFSelectNew from "@/components/Input/SelectNew";
import MainActions from "@/components/MainActions";
import {
	PlayerRow,
	SectionHeader,
	getRoleOrder,
} from "@/pages/Manager/Match/ConvocationsTab";

import DoneAllIcon from "@mui/icons-material/DoneAll";

import "@/pages/Manager/Match/ConvocationsTab.scss";

// Stessi stati e stessa riga delle convocazioni di gara
const STATUSES = [
	{value: "Presente", label: "Presente", cls: "present"},
	{value: "Assente", label: "Assente", cls: "absent"},
	{value: "Infortunato", label: "Infortunato", cls: "injured"},
];

const GROUPS = [
	{status: "none", label: "Da segnare", cls: "none"},
	{status: "Presente", label: "Presenti", cls: "present"},
	{status: "Assente", label: "Assenti", cls: "absent"},
	{status: "Infortunato", label: "Infortunati", cls: "injured"},
];

const byRoleAndName = (a, b) =>
	getRoleOrder(a) - getRoleOrder(b) ||
	(a.lastName || "").localeCompare(b.lastName || "");

export default function AttendanceTab({
	trainingData,
	teamPlayers,
	otherPlayers,
	injuredIds,
	editable = true,
	onStatusClick, // (playerId, status | null, attendanceId) → Promise
	onAllPresent, // (playerIds) → Promise
}) {
	const [busyIds, setBusyIds] = useState(new Set());

	const convMap = useMemo(
		() =>
			Object.fromEntries(
				(trainingData?.trainingConvocations || []).map((c) => [c.playerId, c]),
			),
		[trainingData?.trainingConvocations],
	);

	const teamIds = useMemo(
		() => new Set((teamPlayers || []).map((p) => p.id)),
		[teamPlayers],
	);

	const players = useMemo(
		() =>
			[
				...(teamPlayers || []),
				...(otherPlayers || []).filter((p) => convMap[p.id]),
			].sort(byRoleAndName),
		[teamPlayers, otherPlayers, convMap],
	);

	const groups = useMemo(() => {
		const g = {none: [], Presente: [], Assente: [], Infortunato: []};
		players.forEach((p) => (g[convMap[p.id]?.status] || g.none).push(p));
		return g;
	}, [players, convMap]);

	const addableOthers = (otherPlayers || [])
		.filter((p) => !convMap[p.id])
		.map((p) => ({
			value: p.id,
			label: `${p.firstName || ""} ${p.lastName || ""}`.trim(),
		}));

	const handleClick = async (playerId, status, attendanceId, isActive) => {
		setBusyIds((prev) => new Set(prev).add(playerId));
		try {
			await onStatusClick(playerId, isActive ? null : status, attendanceId);
		} finally {
			setBusyIds((prev) => {
				const next = new Set(prev);
				next.delete(playerId);
				return next;
			});
		}
	};

	// "Tutti presenti" segna solo chi è da segnare e non ha infortuni attivi
	const toMarkPresent = groups.none
		.filter((p) => !injuredIds?.has(p.id))
		.map((p) => p.id);

	return (
		<div className="cv-wrap">
			{editable && (
				<div className="cv-roster-controls">
					<span>
						{trainingData?.rosterName
							? `Rosa: ${trainingData.rosterName}`
							: "Giocatori dell'allenamento"}
					</span>
					<MainActions
						actions={[
							{
								icon: <DoneAllIcon />,
								label: "Tutti presenti",
								variant: "outlined",
								disabled: !toMarkPresent.length,
								onClick: () => onAllPresent(toMarkPresent),
							},
						]}
					/>
				</div>
			)}

			{GROUPS.map(({status, label, cls}) => {
				const list = groups[status];
				if (!list.length) return null;
				return (
					<div key={status} className="cv-group">
						<SectionHeader label={label} count={list.length} cls={cls} />
						<div className="cv-group__body">
							{list.map((p) => (
								<PlayerRow
									key={p.id}
									player={p}
									statuses={editable ? STATUSES : []}
									groupLabel="Presenza"
									currentStatus={convMap[p.id]?.status ?? null}
									attendanceId={convMap[p.id]?.id}
									onStatusClick={handleClick}
									busy={busyIds.has(p.id)}
									otherRoster={!teamIds.has(p.id)}
									medicalInjury={
										injuredIds?.has(p.id) &&
										convMap[p.id]?.status !== "Infortunato"
									}
								/>
							))}
						</div>
					</div>
				);
			})}

			{!players.length && (
				<EmptyData>Nessun giocatore nella rosa dell'allenamento.</EmptyData>
			)}

			{editable && addableOthers.length > 0 && (
				<VLFSelectNew
					label="Aggiungi da altre rose"
					options={addableOthers}
					value={null}
					onChange={(o) => o && onStatusClick(o.value, "Presente")}
				/>
			)}
		</div>
	);
}
